import { faqData } from "@/lib/faq-data";
import { countries } from "@/lib/countries-data";
import { legislationData, lawProvisions } from "@/lib/legislation-data";
import { regionalCentersData } from "@/lib/regional-centers-data";
import { currentVisaBulletin } from "@/lib/visa-bulletin-data";
import { articles } from "@/lib/articles-data";

export interface SearchResult {
  id: string;
  type: "faq" | "country" | "legislation" | "provision" | "regional-center" | "page" | "article";
  title: string;
  snippet: string;
  url: string;
  category?: string;
}

interface IndexedItem extends SearchResult {
  searchableText: string;
  titleText: string;
  centerNames?: string;
}

let index: IndexedItem[] | null = null;

function buildIndex(): IndexedItem[] {
  const items: IndexedItem[] = [];

  for (const faq of faqData) {
    items.push({
      id: `faq-${faq.id}`,
      type: "faq",
      title: faq.question,
      snippet: faq.answer,
      url: "/knowledge-base",
      category: faq.category,
      searchableText: `${faq.question} ${faq.answer} ${faq.category}`.toLowerCase(),
      titleText: faq.question.toLowerCase(),
    });
  }

  for (const country of countries) {
    items.push({
      id: `country-${country.code}`,
      type: "country",
      title: `${country.name} - EB-5 Investor Guide`,
      snippet: `EB-5 visa information for investors from ${country.name} (${country.nativeName}). Current wait time: ${country.eb5Stats.currentWaitTime}.`,
      url: `/countries/${country.code}`,
      category: "Country Guides",
      searchableText: `${country.name} ${country.nativeName} ${country.code} eb-5 investor guide ${country.eb5Stats.currentWaitTime}`.toLowerCase(),
      titleText: `${country.name} eb-5 investor guide`.toLowerCase(),
    });
  }

  for (const leg of legislationData) {
    items.push({
      id: `legislation-${leg.id}`,
      type: "legislation",
      title: leg.title,
      snippet: leg.summary,
      url: "/legislation",
      category: "Legislation",
      searchableText: `${leg.title} ${leg.summary} ${leg.impact}`.toLowerCase(),
      titleText: leg.title.toLowerCase(),
    });
  }

  for (const provision of lawProvisions) {
    items.push({
      id: `provision-${provision.id}`,
      type: "provision",
      title: provision.title,
      snippet: provision.whatItMeans,
      url: "/legislation",
      category: "Legislation",
      searchableText: `${provision.title} ${provision.before} ${provision.after} ${provision.whatItMeans} ${provision.practicalTip}`.toLowerCase(),
      titleText: provision.title.toLowerCase(),
    });
  }

  for (const [, stateData] of Object.entries(regionalCentersData)) {
    const centerCount = stateData.centers.length;
    const allCenterNames = stateData.centers.map((c) => c.name).join(" ");
    items.push({
      id: `rc-${stateData.abbreviation}`,
      type: "regional-center",
      title: `${stateData.name} - ${centerCount} Regional Center${centerCount !== 1 ? "s" : ""}`,
      snippet: `${stateData.name} has ${centerCount} USCIS-designated EB-5 regional center${centerCount !== 1 ? "s" : ""} available for immigrant investor projects.`,
      url: "/regional-centers",
      category: "Regional Centers",
      searchableText: `${stateData.name} ${stateData.abbreviation} regional center ${allCenterNames}`.toLowerCase(),
      titleText: `${stateData.name} regional centers`.toLowerCase(),
      centerNames: allCenterNames.toLowerCase(),
    });
  }

  for (const entry of currentVisaBulletin.finalActionDates) {
    items.push({
      id: `bulletin-${entry.country.replace(/\s+/g, "-").toLowerCase()}`,
      type: "page",
      title: `${currentVisaBulletin.month} ${currentVisaBulletin.year} Visa Bulletin - ${entry.country}`,
      snippet: `Final Action Dates: Unreserved ${entry.unreserved}, Rural TEA ${entry.rural}, High Unemployment TEA ${entry.highUnemployment}, Infrastructure ${entry.infrastructure}.`,
      url: "/visa-bulletin",
      category: "Visa Bulletin",
      searchableText: `${entry.country} visa bulletin priority date final action ${entry.unreserved} ${entry.rural} ${entry.highUnemployment} ${entry.infrastructure} unreserved rural tea high unemployment infrastructure set-aside ${currentVisaBulletin.month} ${currentVisaBulletin.year}`.toLowerCase(),
      titleText: `${entry.country} visa bulletin`.toLowerCase(),
    });
  }

  for (const article of articles) {
    const sectionText = article.sections.map(s => s.heading + " " + s.content).join(" ");
    items.push({
      id: `article-${article.slug}`,
      type: "article",
      title: article.title,
      snippet: article.excerpt,
      url: `/news/${article.slug}`,
      category: article.category,
      searchableText: `${article.title} ${article.excerpt} ${article.category} ${article.targetKeywords.join(" ")} ${sectionText}`.toLowerCase(),
      titleText: `${article.title} ${article.targetKeywords.join(" ")}`.toLowerCase(),
    });
  }

  const staticPages: { title: string; snippet: string; url: string }[] = [
    {
      title: "EB-5 Investor Visa Program Overview",
      snippet: "Comprehensive guide to the EB-5 Immigrant Investor Program, including investment requirements, processing times, and pathways to U.S. permanent residency.",
      url: "/",
    },
    {
      title: "EB-5 Visa Bulletin - Priority Dates & Wait Times",
      snippet: "Current EB-5 visa bulletin with final action dates and dates for filing for all countries and categories including rural, high unemployment, and infrastructure set-asides.",
      url: "/visa-bulletin",
    },
    {
      title: "Direct EB-5 vs Regional Center Comparison",
      snippet: "Compare direct EB-5 investment with regional center investment options. Understand the differences in job creation requirements, management involvement, and investment structure.",
      url: "/direct-eb5",
    },
    {
      title: "Gold Card vs EB-5 Visa Comparison",
      snippet: "Comprehensive comparison of the Gold Card program versus the EB-5 Immigrant Investor Visa. Learn why EB-5's 35-year legal foundation, lower investment minimum, family inclusion, and path to citizenship make it the more secure choice.",
      url: "/gold-card",
    },
    {
      title: "EB-5 News",
      snippet: "In-depth analysis of EB-5 program developments, policy changes, investment trends, Gold Card comparisons, and practical guidance for immigrant investors.",
      url: "/news",
    },
    {
      title: "EB-5 Community Forum & Discussions",
      snippet: "Join the EB-5 investor community forum to discuss experiences, ask questions, and share insights about the immigrant investor visa program.",
      url: "/forum",
    },
    {
      title: "EB-5 Knowledge Base & FAQ",
      snippet: "Frequently asked questions about the EB-5 program covering investment amounts, processing times, regional centers, set-aside categories, and more.",
      url: "/knowledge-base",
    },
  ];

  for (const page of staticPages) {
    items.push({
      id: `page-${page.url}`,
      type: "page",
      title: page.title,
      snippet: page.snippet,
      url: page.url,
      category: "Pages",
      searchableText: `${page.title} ${page.snippet}`.toLowerCase(),
      titleText: page.title.toLowerCase(),
    });
  }

  return items;
}

export function searchContent(query: string, limit: number = 20): SearchResult[] {
  const trimmed = query.trim();
  if (!trimmed) return [];

  if (!index) {
    index = buildIndex();
  }

  const words = trimmed.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  const scored: { item: IndexedItem; score: number }[] = [];

  for (const item of index) {
    let allMatch = true;
    let score = 0;

    for (const word of words) {
      const inTitle = item.titleText.includes(word);
      const inText = item.searchableText.includes(word);

      if (!inTitle && !inText) {
        allMatch = false;
        break;
      }

      if (inTitle) score += 3;
      if (inText) score += 1;
    }

    if (allMatch) {
      scored.push({ item, score });
    }
  }

  scored.sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map(({ item }) => ({
    id: item.id,
    type: item.type,
    title: item.title,
    snippet: item.snippet,
    url: item.url,
    category: item.category,
  }));
}
